import {
    Address,
    Customer,
    Product,
    FinalizeProductDTO,
    AddressDTO,
    PrivateCustomerDTO,
    CompanyDTO,
    PrivateCustomer,
    Company,
    BookingDTO,
    PackageDTO,
    Booking,
    Package,
    Partner,
    PartnerDTO,
    Service,
    ServiceDTO,
    VoucherDTO,
    VoucherOrNone,
    CustomerType,
    CustomerDTO,
    BasisTypeDTO,
    PeriodDTO,
    BookableSchedules,
} from '@raskrask-dk/bookingflow-state-machine'
import { Period, RRDate } from '@raskrask-dk/raskrask-utils'
import { BasisTypeBF } from '../models'

export class BfsmRepoAdaptors {
    static convertToFinalizeProductDTO = (
        customer: Customer,
        product: Product,
        sendMessage: boolean,
        reepayPaymentMethod?: string,
        promoCode?: string,
        voucher?: VoucherOrNone,
        bookingReasons?: any[]
    ): FinalizeProductDTO => {
        const paymentMethod =
            reepayPaymentMethod === 'Card'
                ? 'sca-card'
                : reepayPaymentMethod === 'Mobile Pay'
                ? 'mobilepay'
                : undefined
        return {
            customer: this.convertCustomerToDTO(customer),
            product: this.convertProductToDTO(product, bookingReasons),
            promoCode: promoCode,
            reepayPaymentMethod: paymentMethod,
            sendMessage: sendMessage,
            voucher: this.convertVoucherToDTO(customer, product, voucher),
        } as FinalizeProductDTO
    }

    static convertCustomerToDTO = (
        input: Customer
    ): PrivateCustomerDTO | CompanyDTO => {
        const customerType = input.type
        switch (customerType) {
            case 'Private': {
                const { email, name, phone, type } = input as PrivateCustomer
                return {
                    email: email,
                    name: name,
                    phone: phone,
                    type: type,
                } as PrivateCustomerDTO
            }
            case 'Company': {
                const { email, name, phone, type, adminName } = input as Company
                return {
                    email: email,
                    companyName: name,
                    adminName: adminName,
                    phone: phone,
                    type: type,
                } as CompanyDTO
            }
        }
    }

    static convertProductToDTO = (
        product: Product,
        bookingReasons?: any[]
    ): BookingDTO | PackageDTO => {
        const productType = product.type
        switch (productType) {
            case 'Booking': {
                const {
                    // bookingReasons,
                    partner,
                    problemDescription,
                    service,
                    serviceDate,
                    type,
                    orderAddress,
                } = product as Booking
                return {
                    // bookingReasons: bookingReasons,
                    partner: this.convertPartnerToDTO(partner),
                    problemDescription: problemDescription,
                    service: this.convertServiceToDTO(service),
                    serviceDate: serviceDate.toString('yyyy-MM-dd HH:mm:ss'),
                    type: type,
                    address: this.toAddressDTO(orderAddress),
                    bookingReasons:
                        bookingReasons?.map((br: any) => br['_id']) || [],
                }
            }
            case 'Package': {
                const {
                    id,
                    multiplier,
                    packageName,
                    partner,
                    service,
                    type,
                    discountPercentage,
                } = product as Package
                return {
                    id: id,
                    multiplier: multiplier,
                    packageName: packageName,
                    partner: this.convertPartnerToDTO(partner),
                    service: this.convertServiceToDTO(service),
                    type: type,
                    discountPercentage: discountPercentage,
                }
            }
        }
        throw new Error('Something went wrong during creating ProductDTO')
    }

    static convertPartnerToDTO = (input: Partner): PartnerDTO => {
        const { id, alias } = input as Partner
        return {
            id: id,
            alias: alias,
        }
    }

    static convertServiceToDTO = (input: Service): ServiceDTO => {
        const { id, name, duration } = input as Service
        return {
            id: id,
            name: name,
            duration: duration.minutes,
        }
    }

    static convertVoucherToDTO = (
        customer: Customer,
        product: Product,
        voucher?: VoucherOrNone
    ): VoucherDTO | undefined => {
        const customerType = customer.type
        const productType = product.type
        const isPrivate = customerType == 'Private'
        const isCompany = customerType == 'Company'
        const isVoucher = voucher !== undefined && voucher?.type != 'NONE'
        const isCompanyBooking = isCompany && productType == 'Booking'
        if (isVoucher && (isPrivate || isCompanyBooking)) {
            return voucher as VoucherDTO
        }
        return undefined
    }

    static toAddressDTO(address: Address): AddressDTO {
        return {
            geoLat: address.geoLat,
            geoLng: address.geoLng,
            zipCode: address.zipCode.toString(),
            address: address.address,
            floor: address.floor,
        }
    }

    static toCustomerDTO(
        customerType: CustomerType,
        address: Address
    ): CustomerDTO {
        return {
            customerType: customerType,
            address: BfsmRepoAdaptors.toAddressDTO(address),
        }
    }

    static toBasisTypeDTO(basisType: BasisTypeBF): BasisTypeDTO {
        return {
            id: basisType.id.toString(),
            name: basisType.name,
            shortestDuration: '0',
            medium: 'InPerson',
        }
    }

    static toPeriodDTO(period: Period): PeriodDTO {
        return {
            start: period.from.toString('yyyy-MM-dd HH:mm:ss'),
            days: period.duration().toString('d'),
        }
    }

    static toBookableSchedulesDTO(
        address: Address,
        basisType: BasisTypeBF,
        customerType: CustomerType,
        startDate: RRDate,
        endDate: RRDate,
        filter?: {
            profileIds: number[]
        }
    ): BookableSchedules {
        return {
            customer: BfsmRepoAdaptors.toCustomerDTO(customerType, address),
            basisType: BfsmRepoAdaptors.toBasisTypeDTO(basisType),
            period: new Period(startDate, endDate),
            filters: filter,
        }
    }
}
